import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EOrderType } from '@aqx-core/common/api/order';

import { priceToPoints, stopLossPointsToPrice, takeProfitPointsToPrice } from '../../../../calculation/order';
import { IPricePointsCalculationParams } from './types';

// Helper function to check if order type is a BUY order
const isBuyOrder = (orderType: EOrderType): boolean => {
  return [
    EOrderType.BUY,
    EOrderType.BUY_LIMIT,
    EOrderType.BUY_STOP,
    EOrderType.BUY_STOP_LIMIT,
    EOrderType.BUY_MARKET_TO_LIMIT
  ].includes(orderType);
};

// Helper function to check if order type is a SELL order
const isSellOrder = (orderType: EOrderType): boolean => {
  return [
    EOrderType.SELL,
    EOrderType.SELL_LIMIT,
    EOrderType.SELL_STOP,
    EOrderType.SELL_STOP_LIMIT,
    EOrderType.SELL_MARKET_TO_LIMIT
  ].includes(orderType);
};

// Helper function to validate input parameters
const validatePriceInputs = (priceValue: number, entryPriceValue: number, pointStep: number): boolean => {
  return [priceValue, entryPriceValue, pointStep].every((v) => v !== null && v !== undefined && !Number.isNaN(v));
};

// Helper function to validate points inputs
const validatePointsInputs = (pointsValue: number, entryPriceValue: number, pointStep: number): boolean => {
  return [pointsValue, entryPriceValue, pointStep].every((v) => v !== null && v !== undefined && !Number.isNaN(v));
};

export const getLivePriceByOrderType = (
  orderType: EOrderType,
  symbolDetail: Partial<ISymbolDetail> | undefined
): number | undefined => {
  if (!symbolDetail || orderType === null || orderType === undefined) {
    return undefined;
  }

  // Based on web-trader working implementation:
  // BUY orders: use bid price for stop loss/take profit calculations
  // SELL orders: use ask price for stop loss/take profit calculations
  if (isBuyOrder(orderType)) {
    return symbolDetail.bid;
  }

  if (isSellOrder(orderType)) {
    return symbolDetail.ask;
  }

  return undefined;
};

// Helper function to calculate stop loss points
const calculateStopLossPoints = (
  resolvedOrderType: EOrderType,
  priceValue: number,
  entryPriceValue: number,
  pointStep: number
): number | undefined => {
  if (isBuyOrder(resolvedOrderType)) {
    // For BUY orders: Stop Loss should be below entry price
    return priceToPoints({
      input: entryPriceValue,
      price: priceValue,
      pointStep
    });
  }
  if (isSellOrder(resolvedOrderType)) {
    // For SELL orders: Stop Loss should be above entry price
    return priceToPoints({
      input: priceValue,
      price: entryPriceValue,
      pointStep
    });
  }
  return undefined;
};

// Helper function to calculate take profit points
const calculateTakeProfitPoints = (
  resolvedOrderType: EOrderType,
  priceValue: number,
  entryPriceValue: number,
  pointStep: number
): number | undefined => {
  if (isBuyOrder(resolvedOrderType)) {
    // For BUY orders: Take Profit should be above entry price
    return priceToPoints({
      input: priceValue,
      price: entryPriceValue,
      pointStep
    });
  }
  if (isSellOrder(resolvedOrderType)) {
    // For SELL orders: Take Profit should be below entry price
    return priceToPoints({
      input: entryPriceValue,
      price: priceValue,
      pointStep
    });
  }
  return undefined;
};

export const calculatePointsFromPrice = (params: IPricePointsCalculationParams): string | undefined => {
  const { price, type, entryPrice, buySell, orderType, pointStep } = params;
  const priceValue = parseFloat(price);
  const entryPriceValue = parseFloat(entryPrice);

  if (!validatePriceInputs(priceValue, entryPriceValue, pointStep)) {
    return undefined;
  }

  let resolvedOrderType: EOrderType;
  if (orderType !== null && orderType !== undefined) {
    resolvedOrderType = orderType;
  } else if (buySell) {
    resolvedOrderType = buySell === 'BUY' ? EOrderType.BUY : EOrderType.SELL;
  } else {
    return undefined;
  }

  let points: number | undefined;

  if (type === 'SL') {
    points = calculateStopLossPoints(resolvedOrderType, priceValue, entryPriceValue, pointStep);
  } else if (type === 'TP') {
    points = calculateTakeProfitPoints(resolvedOrderType, priceValue, entryPriceValue, pointStep);
  }

  // Return points as-is, including negative values to indicate invalid input
  // (matches web-trader behavior where negative points show validation errors)
  return points !== undefined ? points.toString() : undefined;
};

// Helper function to calculate price using specialized functions
const calculatePriceByType = (
  type: 'SL' | 'TP',
  entryPriceValue: number,
  pointsValue: number,
  resolvedOrderType: EOrderType,
  pointStep: number
): number | undefined => {
  if (type === 'SL') {
    return stopLossPointsToPrice({
      input: entryPriceValue,
      points: pointsValue,
      orderType: resolvedOrderType,
      pointStep
    });
  }
  return takeProfitPointsToPrice({
    input: entryPriceValue,
    points: pointsValue,
    orderType: resolvedOrderType,
    pointStep
  });
};

export const calculatePriceFromPoints = (params: {
  points: string;
  type: 'SL' | 'TP';
  entryPrice: string;
  buySell?: string;
  orderType?: EOrderType;
  pointStep: number;
  symbolDetail: Partial<ISymbolDetail> | undefined;
}): string | undefined => {
  const { points, type, entryPrice, buySell, orderType, pointStep, symbolDetail } = params;

  if (!symbolDetail) {
    return undefined;
  }

  const pointsValue = parseFloat(points);
  const entryPriceValue = parseFloat(entryPrice);

  if (!validatePointsInputs(pointsValue, entryPriceValue, pointStep)) {
    return undefined;
  }

  let resolvedOrderType: EOrderType;
  if (orderType !== null && orderType !== undefined) {
    resolvedOrderType = orderType;
  } else if (buySell) {
    resolvedOrderType = buySell === 'BUY' ? EOrderType.BUY : EOrderType.SELL;
  } else {
    return undefined;
  }

  const price = calculatePriceByType(type, entryPriceValue, pointsValue, resolvedOrderType, pointStep);

  if (!price) {
    return undefined;
  }

  return price.toFixed(symbolDetail.decimal);
};
